import React, { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAdminGetStripeProducts,
  useAdminTriggerStripeProductSync,
  getAdminGetStripeProductsQueryKey,
} from '@/api/auth/admin/admin';
import type { StripeProductResponse } from '@/api/auth/missionImpactAuthAPI.schemas';
import { StripeProductLocalizationsDialog } from '@/components/admin/StripeProductLocalizationsDialog';
import { RefreshCw, ChevronRight, Languages, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/services/logger';
import { cn } from '@/lib/utils';

/**
 * Admin page showing Stripe products synced from Stripe
 */
export default function AdminStripeProductsPage() {
  const queryClient = useQueryClient();
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  const [localizationsDialogState, setLocalizationsDialogState] = useState<{
    isOpen: boolean;
    product: StripeProductResponse | null;
  }>({
    isOpen: false,
    product: null,
  });

  // Fetch all Stripe products
  const {
    data: productsData,
    isLoading: isLoadingProducts,
    error: productsError,
  } = useAdminGetStripeProducts({ includeInactive: true });

  const products = productsData?.data?.products || [];

  // Sync mutation
  const { mutate: triggerSync, isPending: isSyncing } = useAdminTriggerStripeProductSync({
    mutation: {
      onSuccess: (response) => {
        toast.success('Product sync started', {
          description: `Instance ID: ${response.data?.instanceId}`,
        });
        // Refresh after a short delay to allow sync to start
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: getAdminGetStripeProductsQueryKey() });
        }, 2000);
      },
      onError: (error: any) => {
        logger.error(error, { context: 'TriggerStripeProductSync' });
        toast.error('Failed to trigger sync: ' + (error.response?.data?.detail || error.message));
      },
    },
  });

  const handleSyncClick = useCallback(() => {
    triggerSync();
  }, [triggerSync]);

  const toggleProductExpansion = useCallback((stripeProductId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(stripeProductId)) {
        next.delete(stripeProductId);
      } else {
        next.add(stripeProductId);
      }
      return next;
    });
  }, []);

  const handleLocalizationsClick = useCallback((product: StripeProductResponse) => {
    setLocalizationsDialogState({ isOpen: true, product });
  }, []);

  const handleLocalizationsDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setLocalizationsDialogState({ isOpen: false, product: null });
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Stripe Products</h1>
          <p className="text-muted-foreground">
            Products and prices synced from Stripe. Add localizations for display in different languages.
          </p>
        </div>
        <Button onClick={handleSyncClick} disabled={isSyncing}>
          <RefreshCw className={cn('h-4 w-4 mr-2', isSyncing && 'animate-spin')} />
          {isSyncing ? 'Syncing...' : 'Sync from Stripe'}
        </Button>
      </div>

      {productsError ? (
        <div className="text-center text-destructive py-8">
          <p>Failed to load products: {(productsError as any)?.message || 'Unknown error'}</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead>Stripe Product ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Prices</TableHead>
                <TableHead>Localizations</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingProducts ? (
                [...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[200px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-[80px]" /></TableCell>
                  </TableRow>
                ))
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No products found. Click &quot;Sync from Stripe&quot; to import products from your Stripe account.
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const isExpanded = expandedProducts.has(product.stripeProductId!);
                  const hasPrices = product.prices && product.prices.length > 0;

                  return (
                    <React.Fragment key={product.stripeProductId}>
                      <TableRow className="group">
                        <TableCell>
                          {hasPrices ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleProductExpansion(product.stripeProductId!)}
                            >
                              <ChevronRight
                                className={cn(
                                  'h-4 w-4 transition-transform',
                                  isExpanded && 'rotate-90'
                                )}
                              />
                            </Button>
                          ) : (
                            <div className="w-8" />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                            {product.stripeProductId}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.description && (
                              <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                                {product.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {product.prices?.length || 0} price{(product.prices?.length || 0) !== 1 ? 's' : ''}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.localizations && product.localizations.length > 0 ? 'default' : 'outline'}>
                            {product.localizations?.length || 0} language{(product.localizations?.length || 0) !== 1 ? 's' : ''}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {product.active ? (
                            <Badge variant="default" className="bg-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <XCircle className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLocalizationsClick(product)}
                            title="Manage localizations"
                          >
                            <Languages className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>

                      {isExpanded && product.prices?.map((price) => (
                        <TableRow key={price.stripePriceId} className="bg-muted/30">
                          <TableCell></TableCell>
                          <TableCell className="font-mono text-sm pl-8">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">&#x2514;</span>
                              <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]">
                                {price.stripePriceId}
                              </code>
                            </div>
                          </TableCell>
                          <TableCell colSpan={2} className="text-sm text-muted-foreground">
                            Price
                          </TableCell>
                          <TableCell>
                            {price.hasEntitlementMapping ? (
                              <Badge variant="default" className="bg-blue-600">
                                Entitlement mapped
                              </Badge>
                            ) : (
                              <Badge variant="outline">
                                No entitlement
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {price.active ? (
                              <Badge variant="default" className="bg-green-600">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Archived</Badge>
                            )}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {localizationsDialogState.product && (
        <StripeProductLocalizationsDialog
          stripeProductId={localizationsDialogState.product.stripeProductId!}
          productName={localizationsDialogState.product.name!}
          localizations={localizationsDialogState.product.localizations || []}
          open={localizationsDialogState.isOpen}
          onOpenChange={handleLocalizationsDialogClose}
        />
      )}
    </div>
  );
}
